/**
 * In-memory transcript scroll position, per conversation and per surface.
 * Survives Chat page ↔ rail remounts within a session so leaving a thread
 * and coming back does not yank the viewport to the latest turn.
 *
 * The page and the rail render the same conversation at different widths, so
 * a pixel offset from one is meaningless in the other — they are remembered
 * separately.
 */

/** How close (px) to the bottom counts as still following the stream. */
export const NEAR_BOTTOM_PX = 96

/** Chat surfaces render the same thread at different widths. */
export type TranscriptSurface = 'page' | 'rail'

export interface TranscriptScrollMemory {
  scrollTop: number
  stickToBottom: boolean
}

export interface ViewportMetrics {
  scrollTop: number
  scrollHeight: number
  clientHeight: number
}

const MAX_ENTRIES = 50
const byKey = new Map<string, TranscriptScrollMemory>()

function keyFor(surface: TranscriptSurface, tabId: string | null): string | null {
  if (tabId === null || tabId === '') return null
  return `${surface}:${tabId}`
}

/** Largest scrollTop the viewport can hold; `0` when the transcript fits. */
export function maxScrollTop(metrics: { scrollHeight: number; clientHeight: number }): number {
  return Math.max(0, metrics.scrollHeight - metrics.clientHeight)
}

/**
 * Read a position worth remembering, or `null` while the transcript is too
 * short to scroll. A pane whose turns have not laid out yet reports "at the
 * bottom" of nothing, which must never overwrite a real offset.
 */
export function readTranscriptScroll(metrics: ViewportMetrics): TranscriptScrollMemory | null {
  const max = maxScrollTop(metrics)
  if (max <= 0) return null
  return {
    scrollTop: metrics.scrollTop,
    stickToBottom: max - metrics.scrollTop <= NEAR_BOTTOM_PX
  }
}

export function rememberTranscriptScroll(
  surface: TranscriptSurface,
  tabId: string | null,
  memory: TranscriptScrollMemory
): void {
  const key = keyFor(surface, tabId)
  if (key === null) return
  byKey.delete(key)
  byKey.set(key, memory)
  while (byKey.size > MAX_ENTRIES) {
    const oldest = byKey.keys().next().value
    if (oldest === undefined) break
    byKey.delete(oldest)
  }
}

export function peekTranscriptScroll(
  surface: TranscriptSurface,
  tabId: string | null
): TranscriptScrollMemory | null {
  const key = keyFor(surface, tabId)
  if (key === null) return null
  return byKey.get(key) ?? null
}

/** Test-only: drop every remembered position. */
export function resetTranscriptScroll(): void {
  byKey.clear()
}

/** True when this conversation should reopen at a saved offset, not the latest turn. */
export function shouldRestoreTranscriptScroll(
  saved: TranscriptScrollMemory | null
): saved is TranscriptScrollMemory {
  return saved !== null && !saved.stickToBottom
}

/**
 * Whether the transcript has grown enough to actually hold `scrollTop`.
 * Assigning past this clamps, which would strand the reader mid-thread.
 */
export function canApplyScrollTop(
  metrics: { scrollHeight: number; clientHeight: number },
  scrollTop: number
): boolean {
  return maxScrollTop(metrics) >= scrollTop
}
